import { sigmoid } from '../v2/logisticRegression.js';
import { fitRegressionTree, findLeaf, predictTree } from './tree.js';
export const DEFAULT_GBM_FIT_OPTIONS = {
    numTrees: 150,
    learningRate: 0.1,
    maxDepth: 3,
    minSamplesLeaf: 20,
    l2: 1,
};
const LOG_ODDS_EPS = 1e-6;
function logOdds(p) {
    const clamped = Math.min(1 - LOG_ODDS_EPS, Math.max(LOG_ODDS_EPS, p));
    return Math.log(clamped / (1 - clamped));
}
/**
 * Fits a gradient-boosted classifier. `y` must be 0/1 labels aligned with
 * `X`'s rows; `featureNames` is stored purely for traceability, matching
 * `LogisticModel`'s own convention.
 */
export function fitGradientBoosting(featureNames, X, y, options = {}) {
    if (X.length !== y.length || X.length === 0) {
        throw new Error('fitGradientBoosting: X and y must be the same non-zero length.');
    }
    if (X.some((row) => row.length !== featureNames.length)) {
        throw new Error('fitGradientBoosting: every row must match featureNames.length.');
    }
    const opts = { ...DEFAULT_GBM_FIT_OPTIONS, ...options };
    const n = X.length;
    const meanY = y.reduce((a, b) => a + b, 0) / n;
    const initialLogOdds = logOdds(meanY);
    const F = new Array(n).fill(initialLogOdds);
    const trees = [];
    for (let m = 0; m < opts.numTrees; m++) {
        const p = F.map(sigmoid);
        const residuals = y.map((yi, i) => yi - p[i]);
        const tree = fitRegressionTree(X, residuals, {
            maxDepth: opts.maxDepth,
            minSamplesLeaf: opts.minSamplesLeaf,
        });
        // Newton-step leaf refinement: replace each leaf's plain residual mean
        // with sum(residuals) / (sum(p*(1-p)) + l2) over the rows that land in
        // it — the closed-form single-step optimum for log loss at that leaf.
        const leafStats = new Map();
        for (let i = 0; i < n; i++) {
            const leafNode = findLeaf(tree, X[i]);
            const weight = p[i] * (1 - p[i]);
            const stats = leafStats.get(leafNode) ?? { sumResidual: 0, sumWeight: 0 };
            stats.sumResidual += residuals[i];
            stats.sumWeight += weight;
            leafStats.set(leafNode, stats);
        }
        for (const [leafNode, stats] of leafStats) {
            if (leafNode.type === 'leaf') {
                leafNode.value = stats.sumResidual / (stats.sumWeight + opts.l2);
            }
        }
        for (let i = 0; i < n; i++) {
            F[i] += opts.learningRate * predictTree(tree, X[i]);
        }
        trees.push(tree);
    }
    return { featureNames, trees, learningRate: opts.learningRate, initialLogOdds };
}
/** Predicts P(y=1) for one raw feature vector. */
export function predictProbabilityGbm(model, rawFeatures) {
    if (rawFeatures.length !== model.featureNames.length) {
        throw new Error('predictProbabilityGbm: feature vector length mismatch with model.');
    }
    let z = model.initialLogOdds;
    for (const tree of model.trees) {
        z += model.learningRate * predictTree(tree, rawFeatures);
    }
    return sigmoid(z);
}
//# sourceMappingURL=gradientBoosting.js.map